import { Model } from "@vuex-orm/core";
import Customer from "@/models/Customer.model";
import { parseISO, formatISO9075 } from "date-fns";
import ActivityDescription from "@/models/ActivityDescription.model";
import { isEntityContainsAllTerms, tidySearchTerm } from "@/utils/filters";
import { tr } from "@/utils/translation";

export default class License extends Model {
  static entity = "licenses";
  static now = new Date();

  static fields() {
    return {
      id: this.attr(null),
      addedAtUtc: this.attr(null),
      version: this.attr(null),
      sfdcLicenseID: this.attr(null),
      customerId: this.attr(null),
      licenseCount: this.attr(0),
      validUntil: this.attr(null),
      isActive: this.attr(false),
      licensedActivities: this.attr([]),
      customer: this.belongsTo(Customer, "customerId"),
      activities: this.hasManyBy(
        ActivityDescription,
        "licensedActivities",
        "id"
      ),
    };
  }

  setActivities(values) {
    this.activities = values;
    let newLicensedActivities = [];
    values.forEach((value) => {
      newLicensedActivities.push(value.id);
    });
    this.licensedActivities = newLicensedActivities;
  }

  get EntityIdentifier() {
    return "License";
  }

  get LicenseTypeForDisplayAndSearch() {
    return tr(`AdminPages.${this.EntityIdentifier}.licenseType`);
  }

  set Customer(value) {
    this.customer = value;
    this.customerId = value.id;
  }

  get Customer() {
    return this.customer;
  }

  get CustomerFullHtmlName() {
    return this.customer?.fullName?.length > 0
      ? this.customer.fullNameHtml
      : "";
  }

  get IsCustomerValid() {
    return !!this.customer;
  }

  get ValidUntil() {
    return this.validUntil ? parseISO(this.validUntil) : null;
  }
  set ValidUntil(value) {
    this.validUntil = formatISO9075(value);
  }

  get AddedAtUtc() {
    return this.addedAtUtc ? parseISO(this.addedAtUtc) : null;
  }
  set AddedAtUtc(value) {
    this.addedAtUtc = formatISO9075(value);
  }

  get IsActiveStatus() {
    return this.isActive && this.ValidUntil > License.now;
  }

  get customRowClasses() {
    return this.IsActiveStatus ? "" : "deactivated";
  }

  static get() {
    return this.api().get("/mmgo/license", { dataKey: "result" });
  }

  static async post(newLicense) {
    return this.api().post("/mmgo/license", newLicense, { dataKey: "result" });
  }

  static async remove(license) {
    return this.api().delete("/mmgo/license/" + license.id, {
      delete: license.id,
      dataKey: "result",
    });
  }

  static async put(updatedLicense) {
    return this.api().put(
      "/mmgo/license/" + updatedLicense.id,
      updatedLicense,
      {
        dataKey: "result",
      }
    );
  }

  /**
   * Entity fields to search into
   */
  get entityFieldsToSearchInto() {
    let fields = [
      this.sfdcLicenseID,
      this.id,
      this.LicenseTypeForDisplayAndSearch,
    ];
    if (this.customer?.fullName) {
      fields.push(tidySearchTerm(this.customer.fullName));
    }
    return fields;
  }

  /**
   * Return true iff all terms correspond to the entity
   * @param {string} searchTerms terms are space separated
   */
  isEntityCorrespondingTo(searchTerms) {
    return isEntityContainsAllTerms(this.entityFieldsToSearchInto, searchTerms);
  }
}
